import { Injectable } from '@angular/core';

import { BehaviorSubject, map, Observable, scan, tap } from 'rxjs';

enum LoaderActions {
    SHOW,
    ADD,
    HIDE,
}

/**
 * Manages the fullscreen loader, handling multiple loading operations and controlling visibility.
 */
@Injectable({
    providedIn: 'root',
})
export class FullScreenLoaderService {
    loading!: Observable<boolean>;

    private keepBackgroundVisibleState: boolean | undefined;
    private loadingSubject = new BehaviorSubject<LoaderActions>(LoaderActions.HIDE);

    constructor() {
        this.loading = this.loadingSubject.pipe(
            // Accumulates the count of active loader actions
            scan<LoaderActions, number>((count, action) => this.calculateLoaderCount(count, action), 0),
            // Maps count to boolean (visibility)
            map((count) => count > 0),
            tap((isLoading) => {
                if (!isLoading) {
                    this.resetBackgroundVisibility();
                }
            })
        );
    }

    /**
     * Displays the loader, and optionally keeps the background visible.
     * Always sets the loader count to 1.
     * @param keepBackgroundVisible - Set to true if you want the background visible during loading.
     */
    show(keepBackgroundVisible = false): void {
        this.keepBackgroundVisibleState = keepBackgroundVisible;
        this.loadingSubject.next(LoaderActions.SHOW);
    }

    /**
     * Displays the loader, incrementing the internal count.
     * Will keep loader active for several operations
     * @param keepBackgroundVisible - Set to true if you want the background visible during loading. Will not be applied if there is current loader active.
     */
    showOrQueue(keepBackgroundVisible = false): void {
        if (this.keepBackgroundVisibleState === undefined) {
            this.keepBackgroundVisibleState = keepBackgroundVisible;
        }
        this.loadingSubject.next(LoaderActions.ADD);
    }

    /**
     * Hides the loader by decrementing the internal count.
     * If the count reaches zero, the loader will be hiden.
     */
    hide(): void {
        this.loadingSubject.next(LoaderActions.HIDE);
    }

    /**
     * Gets the boolean value of the background visibility state
     */
    get keepBackgroundVisible(): boolean {
        return !!this.keepBackgroundVisibleState;
    }

    /**
     * Calculates the next loader count based on the action.
     * Ensures that the count never goes below 0.
     * @param count - The current count of active loaders.
     * @param action - The loader action being performed (SHOW, ADD, HIDE).
     * @returns The updated count after applying the action.
     */
    private calculateLoaderCount(count: number, action: LoaderActions): number {
        switch (action) {
            case LoaderActions.SHOW:
                return 1;
            case LoaderActions.ADD:
                return ++count;
            case LoaderActions.HIDE:
                return Math.max(--count, 0);
            default:
                return 0;
        }
    }

    private resetBackgroundVisibility(): void {
        this.keepBackgroundVisibleState = undefined;
    }
}
